// Lib
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

// Utils
import { elementClassNames, getDomElementId } from '../utils/elementUtil';
import { propIn } from '../../../common/utils/immutableHelper';
import { getElementStyle } from '../../utils/grid/gridUtils';
import { getElementId, getWidth } from '../../../common/elements/utils/elementPropertyUtils';
import { getShowQuickLineTool } from '../quickLine/quickLineUtil';
import { isSingleEditorTaskList } from '../../../common/taskLists/taskListContentVersionUtils';

// Selectors
import { getShowTitle } from './taskListSelector';
import { useAttachedCommentStyles } from '../attachedComment/useAttachedCommentStyles';

// Components
import ColorBar from '../../components/colors/ColorBar';
import ElementUserTag from '../../user/userActivity/ElementUserTag';
import QuickNotesActivityIndicator from '../activity/QuickNotesActivityIndicator';
import QuickLineCreationTool from '../quickLine/QuickLineCreationTool';
import AttachedCommentsDropTarget from '../dnd/elementDropTargets/AttachedCommentsDropTarget';
import AttachedCommentsContainer from '../attachedComment/AttachedCommentsContainer';
import { GroupTranslatorProvider } from '../../components/i18n/GroupTranslationContext';
import TaskListEditorSwitch from './TaskListEditorSwitch';

// Constants
import { ELEMENT_DEFAULT_WIDTH } from '../../../common/elements/elementConstants';

// Style
import './TaskList.scss';

const TaskList = (props) => {
    const {
        currentBoardId,
        element,
        gridSize,
        inList,
        elementEvents,
        isEditingChild,
        isEditing,
        isSelected,
        tempSize,
        isPresentational,
        permissions,
        getContextZoomScale,
        getContextZoomTranslationPx,
    } = props;

    const colorName = propIn(['content', 'color'], element);

    const showTitle = !isSingleEditorTaskList(element) && getShowTitle(element);

    const taskClasses = classNames('TaskList drag-handle contrast-shadow', {
        // TODO-SINGLE-TASK-LIST - Remove "show-title" and "no-title" classes
        'show-title': showTitle,
        'no-title': !showTitle,
        'editing-child': isEditingChild,
        'focus-editing': isEditing || isEditingChild,
    });

    const classes = elementClassNames(taskClasses, props);

    const elementId = getElementId(element);

    const savedElementSize = getWidth(element);
    const elementWidth = (tempSize && tempSize.width) || savedElementSize || ELEMENT_DEFAULT_WIDTH;

    // If attached comments have custom colours, we use CSS properties to apply them to the inline comment marks
    const attachedCommentStyles = useAttachedCommentStyles(elementId);

    const elementStyle = {
        ...getElementStyle(elementWidth, gridSize, inList),
        ...attachedCommentStyles,
    };

    return (
        <GroupTranslatorProvider group="elements" keyPrefix="elementTypes.taskList">
            <div
                id={getDomElementId(elementId)}
                className={classes}
                style={elementStyle}
                {...elementEvents}
            >
                <ColorBar colorName={colorName} isSelected={isSelected} />
                <QuickNotesActivityIndicator {...props} />
                <QuickLineCreationTool
                    show={getShowQuickLineTool(props)}
                    elementId={elementId}
                    element={element}
                    currentBoardId={currentBoardId}
                    gridSize={gridSize}
                    getContextZoomScale={getContextZoomScale}
                    getContextZoomTranslationPx={getContextZoomTranslationPx}
                />
                <ElementUserTag {...props} />
                <AttachedCommentsDropTarget
                    gridSize={gridSize}
                    isPresentational={isPresentational}
                    element={element}
                    elementId={elementId}
                    getContextZoomScale={getContextZoomScale}
                />
                <AttachedCommentsContainer
                    elementId={elementId}
                    element={element}
                    gridSize={gridSize}
                    isPresentational={isPresentational}
                    currentBoardId={currentBoardId}
                    permissions={permissions}
                />
                <TaskListEditorSwitch {...props} element={element} elementId={elementId} showTitle={showTitle} />
            </div>
        </GroupTranslatorProvider>
    );
};

TaskList.propTypes = {
    currentBoardId: PropTypes.string,
    element: PropTypes.object.isRequired,
    childElementIds: PropTypes.array,
    gridSize: PropTypes.number,
    isSelected: PropTypes.bool,
    isSingleSelected: PropTypes.bool,
    isPresentational: PropTypes.bool,
    inList: PropTypes.string,
    className: PropTypes.string,
    elementEvents: PropTypes.object,
    tempSize: PropTypes.object,
    permissions: PropTypes.number,
    isPresentationModeEnabled: PropTypes.bool,

    moveToTrashHandler: PropTypes.func,
    changeIndentationHandler: PropTypes.func,
    goToPreviousTaskHandler: PropTypes.func,
    goToNextTaskHandler: PropTypes.func,
    onStartBackspaceHandler: PropTypes.func,
    onEndDeleteHandler: PropTypes.func,
    onTaskListUpdatedHandler: PropTypes.func,

    dispatchUpdateElement: PropTypes.func,
    saveContent: PropTypes.func,
    onTaskToggle: PropTypes.func,
    isEditable: PropTypes.bool,
    isEditing: PropTypes.bool,
    isEditingChild: PropTypes.bool,

    setParentHoveredChildAcceptsDrop: PropTypes.func,
    getContextZoomScale: PropTypes.func,
    getContextZoomTranslationPx: PropTypes.func,

    selectThisElement: PropTypes.func,
};

export default TaskList;
